/**
 * The open world's five seamless regions (docs/design/2026-10-11-open-world-plan.md, option 2): each
 * about 1.5 km, one climate, joined by four seam gates. Every placed `ZoneId` is a named place inside one
 * region with a rect in that region's own metres (origin top-left, x east, y south). Data only.
 */
import type { ZoneId } from './zones';

export type RegionId = 'centre' | 'north' | 'west' | 'east' | 'south';
export type Climate = 'plains' | 'snow' | 'delta' | 'desert' | 'forest';

export interface RegionDef {
  readonly id: RegionId;
  readonly name: string;
  /** Metres east / south. */
  readonly w: number;
  readonly h: number;
  readonly climate: Climate;
}

export const REGIONS: Readonly<Record<RegionId, RegionDef>> = {
  centre: { id: 'centre', name: 'ทุ่งศิลากลาง', w: 1500, h: 1500, climate: 'plains' },
  north: { id: 'north', name: 'แดนเขาเมฆขาว', w: 1800, h: 1200, climate: 'snow' },
  west: { id: 'west', name: 'ลุ่มน้ำสายธาร', w: 1500, h: 1800, climate: 'delta' },
  east: { id: 'east', name: 'แดนทรายชาด', w: 1500, h: 1500, climate: 'desert' },
  south: { id: 'south', name: 'ป่าโคมใต้', w: 1800, h: 1200, climate: 'forest' },
};
export const REGION_IDS: readonly RegionId[] = ['centre', 'north', 'west', 'east', 'south'];

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface Place {
  readonly zone: ZoneId;
  readonly region: RegionId;
  readonly rect: Rect;
}

const p = (zone: ZoneId, region: RegionId, x: number, y: number, w: number, h: number): Place => ({
  zone,
  region,
  rect: { x, y, w, h },
});

/** Every placed zone (the live map set; legacy greybox zones such as yanyu, zhuming, demoPlaza are not placed). */
export const PLACES: readonly Place[] = [
  // centre: the hub plaza (ลานศิลาเก้ากระบี่); the capital site and origin village are reserved by later pieces
  p('plains', 'centre', 558, 558, 384, 384),
  // north
  p('city-snow', 'north', 750, 120, 300, 240),
  p('sect-mist-peak', 'north', 180, 450, 240, 240),
  p('sect-bell-stone', 'north', 780, 630, 240, 240),
  p('sect-snow-pine', 'north', 1350, 450, 240, 240),
  // west
  p('city-trade', 'west', 900, 450, 300, 300),
  p('city-sea', 'west', 150, 1320, 300, 300),
  p('town-c', 'west', 525, 840, 240, 240),
  p('sect-bamboo-tide', 'west', 225, 300, 240, 240),
  p('sect-pine-ink', 'west', 1050, 1350, 240, 240),
  // east
  p('town-d', 'east', 150, 600, 240, 240),
  p('city-desert', 'east', 900, 600, 300, 300),
  p('sect-cinnabar', 'east', 900, 150, 240, 240),
  p('sect-dim-moon', 'east', 600, 1125, 240, 240),
  // south
  p('town-b', 'south', 750, 150, 240, 240),
  p('sect-lantern-road', 'south', 1350, 750, 240, 240),
];

export interface SeamEnd {
  readonly region: RegionId;
  /** Waystation centre, region metres. */
  readonly x: number;
  readonly y: number;
}

export interface SeamGate {
  readonly id: 'gate-n' | 'gate-w' | 'gate-e' | 'gate-s';
  readonly name: string;
  readonly kind: 'pass' | 'ferry' | 'canyon' | 'bridge';
  /** Carriage / boat ride, or on foot (the rope bridge). */
  readonly ride: 'carriage' | 'boat' | 'foot';
  readonly a: SeamEnd;
  readonly b: SeamEnd;
}

export const SEAM_GATES: readonly SeamGate[] = [
  { id: 'gate-n', name: 'ด่านช่องเขา', kind: 'pass', ride: 'carriage', a: { region: 'centre', x: 750, y: 60 }, b: { region: 'north', x: 900, y: 1140 } },
  { id: 'gate-w', name: 'ท่าข้ามสายธาร', kind: 'ferry', ride: 'boat', a: { region: 'centre', x: 60, y: 750 }, b: { region: 'west', x: 1440, y: 900 } },
  { id: 'gate-e', name: 'ประตูหุบผาแดง', kind: 'canyon', ride: 'carriage', a: { region: 'centre', x: 1440, y: 750 }, b: { region: 'east', x: 60, y: 750 } },
  { id: 'gate-s', name: 'สะพานแขวนป่า', kind: 'bridge', ride: 'foot', a: { region: 'centre', x: 750, y: 1440 }, b: { region: 'south', x: 900, y: 60 } },
];

const BY_ZONE = new Map<ZoneId, Place>(PLACES.map((pl) => [pl.zone, pl]));

/** The place of a zone, or undefined for a zone not on the region map. */
export const placeOf = (zone: ZoneId): Place | undefined => BY_ZONE.get(zone);

/** The region a zone sits in, or undefined for a zone not on the region map. */
export const regionOf = (zone: ZoneId): RegionId | undefined => BY_ZONE.get(zone)?.region;
